import PriceBreakDown from 'components/receipt/PriceBreakDown';
import RefundHistoryList from 'components/receipt/RefundHistory';
import RetryPaymentSummary from 'components/receipt/RetryPaymentSummary';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import usePaymentDetails from 'hooks/usePaymentDetails';
import React, { useContext } from 'react';
import { View } from 'react-native';
import { Divider } from 'styles/CommonLayoutStyles';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import {
  BillingItem,
  ChargingSession,
  CountryCode,
  Maybe,
  ParkingSession,
  PaymentType,
  Receipt,
} from 'types/generatedSchemaTypes';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { getPaymentMethodProps } from 'utils/paymentUtils';

type PaymentDetailsProps = {
  testID: string;
  parkingSession?: ParkingSession;
  receiptData?: Receipt;
  chargingSession?: ChargingSession;
  isPaymentFailed: boolean;
  transactionId?: string;
  chargingPaymentType?: Maybe<PaymentType>;
  showTitle?: boolean;
};

const PaymentDetails: React.FC<PaymentDetailsProps> = ({
  testID,
  parkingSession,
  receiptData,
  chargingSession,
  isPaymentFailed,
  transactionId,
  chargingPaymentType,
  showTitle,
}) => {
  const {
    flags: { retry_payment },
  } = useContext(FeatureFlagsContext);
  const showRetry = showFeatureFlag(retry_payment);

  const session = parkingSession ?? chargingSession;
  const paymentMethodProps = getPaymentMethodProps({
    session,
    receiptData,
    chargingPaymentType,
    transactionId,
    totalAmount:
      parkingSession?.price?.totalAmount || chargingSession?.priceInCents,
  });
  const paymentDetails = usePaymentDetails(paymentMethodProps);

  const checkPaymentDate = () => {
    const receipt = parkingSession?.receipt || receiptData;
    if (!receipt) {
      return undefined;
    }
    return new Date(receipt.created as Date);
  };

  const energyUsed =
    receiptData?.items?.[0]?.energyConsumedWatts ||
    chargingSession?.energyInWatts;

  const refundData =
    receiptData?.refundHistory || parkingSession?.receipt?.refundHistory || [];

  return (
    <View testID={testID}>
      <PriceBreakDown
        priceConfig={parkingSession?.priceConfig}
        billingItems={receiptData?.items as [BillingItem]}
        paymentDate={checkPaymentDate()}
        price={parkingSession?.price || (receiptData?.items as [BillingItem])}
        countryCode={
          parkingSession?.countryCode ||
          chargingSession?.countryCode ||
          (receiptData?.countryCode as CountryCode)
        }
        energyUsed={energyUsed as number}
        isFailedChargingSession={!!chargingSession}
        failedChargingSessionPrice={chargingSession?.priceInCents || 0}
        isPaymentFailed={isPaymentFailed}
        transactionId={transactionId}
        showTitle={showTitle}
        paymentMethodName={paymentDetails?.paymentMethodName}
        paymentMethodLogoName={paymentDetails?.paymentMethodLogoName}
        paymentType={paymentDetails?.paymentType}
      />
      {refundData.length > 0 && (
        <>
          <ColumnWithMargins ml={-20} mr={-20} mb={10}>
            <Divider size={10} />
          </ColumnWithMargins>
          <RefundHistoryList
            refundData={refundData}
            countryCode={
              parkingSession?.countryCode ||
              chargingSession?.countryCode ||
              (receiptData?.countryCode as CountryCode)
            }
          />
        </>
      )}
      {isPaymentFailed && showRetry && (
        <RetryPaymentSummary
          totalAmount={
            parkingSession?.price?.totalAmount ??
            (chargingSession?.priceInCents || 0) / 100
          }
          countryCode={
            (parkingSession?.countryCode ||
              chargingSession?.countryCode) as CountryCode
          }
        />
      )}
    </View>
  );
};

export default PaymentDetails;
